import { Injectable } from '@angular/core';
import { BaseSignalStore } from '@core/state/base-store';
import { FileTrackingDto } from '@shared/models/dto/api-dtos';

/**
 * State interface for File Tracking store
 * Manages all tracking steps, current step, and the associated succession file ID
 */
export interface FileTrackingState {
  /** All tracking steps for the succession file */
  steps: FileTrackingDto[];
  /** The current/latest tracking step */
  currentStep: FileTrackingDto | null;
  /** The succession file ID being tracked */
  successionFileId: number | null;
}

/**
 * Store for managing File Tracking state
 * Handles data, loading, error states for file tracking information
 * Provides reactive selectors for components to subscribe to state changes
 */
@Injectable({ providedIn: 'root' })
export class FileTrackingStore extends BaseSignalStore<FileTrackingState> {
  constructor() {
    super({
      steps: [],
      currentStep: null,
      successionFileId: null
    });
  }

  // Computed signal selectors (reactive, use in templates with signals)
  readonly steps = this.select(state => state.steps);
  readonly currentStep = this.select(state => state.currentStep);
  readonly successionFileId = this.select(state => state.successionFileId);
  readonly isEmpty = this.select(state => state.steps.length === 0);
  readonly hasCurrentStep = this.select(state => state.currentStep !== null);

  // Observable selectors for RxJS integration (use in components with async pipe)
  readonly steps$ = this.select$(state => state.steps);
  readonly currentStep$ = this.select$(state => state.currentStep);
  readonly successionFileId$ = this.select$(state => state.successionFileId);
  readonly isEmpty$ = this.select$(state => state.steps.length === 0);
  readonly hasCurrentStep$ = this.select$(state => state.currentStep !== null);

  /**
   * Sets all tracking steps for the succession file
   * @param steps Array of FileTrackingDto representing all steps
   */
  setFileTrackingSteps(steps: FileTrackingDto[]): void {
    this.setState({
      steps
    });
  }

  /**
   * Sets the current tracking step
   * @param currentStep The current/latest FileTrackingDto step
   */
  setCurrentStep(currentStep: FileTrackingDto | null): void {
    this.setState({
      currentStep
    });
  }

  /**
   * Sets the succession file ID being tracked
   * @param successionFileId The ID of the succession file
   */
  setSuccessionFileId(successionFileId: number | null): void {
    this.setState({
      successionFileId
    });
  }

  /**
   * Updates both steps and current step in a single operation
   * Useful when loading complete tracking information
   * 
   * @param steps All tracking steps
   * @param currentStep Current tracking step
   * @param successionFileId Succession file ID
   */
  setFileTracking(
    steps: FileTrackingDto[],
    currentStep: FileTrackingDto | null,
    successionFileId: number | null
  ): void {
    this.setState({
      steps,
      currentStep,
      successionFileId
    });
  }

  /**
   * Resets the store to initial state
   * Clears all tracking data
   */
  override reset(): void {
    super.reset();
  }
}

